import React from 'react';
import { Switch, Route, Redirect } from '@react-router';
import { Box } from '@withjoy/joykit';
import { EventDesignThemingProvider } from '@shared/core/website/theming';
import { ColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { LayoutProvider } from '@shared/context/LayoutProvider';
import { UnlockDialogProvider } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialogProvider';
import { useGuestSiteController } from '@apps/guest/routes/GuestSite/GuestSite.controller';
import { wrapWithGuestSiteHealthProvider } from '@apps/guest/routes/GuestSite/GuestSite.health';
import { useSetEventHandle } from '@shared/utils/eventInfo';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { CashFundGuestLayout } from './components/CashFundGuestLayout';
import { CASH_FUND_GUEST_ROUTES } from './CashFundGuest.routes';
import type { StaticContext } from '@react-router';
import { CashFundsTelemetryProvider } from '../../CashFunds.telemetry';
import { useWebsiteBannerOffset, WebsiteBannerProvider } from '@shared/components/WebsiteBanner';
import { EventDesignJoyKitThemeOverridesProvider } from '@shared/core/website/theming/EventDesignJoyKitThemeOverridesProvider';
import { TemplatePreviewProvider } from '@apps/guest/routes/GuestSite/TemplatePreviewContext';

import { LazyCashFundPurchaseConfirmationWrapper } from '../CashFundPurchaseConfirmation/CashFundPurchaseConfirmationWrapper.lazy';

export interface CashFundGuestProps
  extends Readonly<{
    eventHandle: string;
    fundId: string;
    staticContext?: StaticContext;
  }> {}

const CashFundGuestWithProviders: React.FC<CashFundGuestProps> = wrapWithGuestSiteHealthProvider(({ eventHandle, fundId, staticContext }) => {
  useSetEventHandle(eventHandle);

  const { event, layout, setLoadingTheme } = useGuestSiteController({
    eventHandle,
    staticContext,
  });

  const { bannerOffset } = useWebsiteBannerOffset();

  if (!event || !layout) {
    return <JoyLogoLoader loaderKey="cashfund-guest" />;
  }

  return (
    <Box paddingTop={bannerOffset}>
      <Switch>
        <Route
          path={CASH_FUND_GUEST_ROUTES.success.path}
          render={() => {
            return <LazyCashFundPurchaseConfirmationWrapper />;
          }}
        />
        <Route
          path={CASH_FUND_GUEST_ROUTES.fund.path}
          exact
          render={() => {
            return (
              <LayoutProvider
                layout={layout}
                brannanNavigation={event.eventDesign?.brannanNavigation}
              >
                <EventDesignThemingProvider eventDesign={event.eventDesign}>
                  <UnlockDialogProvider>
                    <ColorPaletteProvider eventDesign={event.eventDesign}>
                      <EventDesignJoyKitThemeOverridesProvider>
                        <CashFundGuestLayout
                          fundId={fundId}
                          event={event}
                          setLoadingTheme={setLoadingTheme}
                        />
                      </EventDesignJoyKitThemeOverridesProvider>
                    </ColorPaletteProvider>
                  </UnlockDialogProvider>
                </EventDesignThemingProvider>
              </LayoutProvider>
            );
          }}
        />
        <Route render={() => <Redirect to={`/${eventHandle}`} />} />
      </Switch>
    </Box>
  );
});

const CashFundGuest: React.FC<CashFundGuestProps> = props => {
  return (
    <CashFundsTelemetryProvider>
      <WebsiteBannerProvider>
        <TemplatePreviewProvider>
          <CashFundGuestWithProviders {...props} />
        </TemplatePreviewProvider>
      </WebsiteBannerProvider>
    </CashFundsTelemetryProvider>
  );
};

export { CashFundGuest };

export default CashFundGuest;
